import { Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ContentWrapper, ModuleLanding } from '@textile-erp/ui';
import { modules } from '../../config/modules.config';
import { PinnedLanding } from '../../components/pinned-landing';
import { RecentLanding } from '../../components/recent-landing';
import { RouteGuard } from '../../components/RouteGuard';

// Loading fallback component
const ModuleLoadingFallback = ({ moduleName }: { moduleName?: string }) => {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">
          {moduleName ? `Loading ${moduleName}...` : 'Loading...'}
        </p>
      </div>
    </div>
  );
};


// Module Landing Page Component
const ModuleLandingPage = ({ moduleId }: { moduleId: string }) => {
  const navigate = useNavigate();
  const config = modules[moduleId];

  if (!config) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!config.categories || config.categories.length === 0) {
    // If no categories, load the module directly
    return <LazyModuleComponent moduleId={moduleId} />;
  }

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: config.name }
  ];

  return (
    <ContentWrapper>
      <ModuleLanding
        title={config.name}
        description={config.description}
        breadcrumbs={breadcrumbs}
        categories={config.categories}
        stats={config.stats}
        onNavigate={(path: string) => navigate(path)}
      />
    </ContentWrapper>
  );
};

// Lazy Module Component
const LazyModuleComponent = ({ moduleId }: { moduleId: string }) => {
  const config = modules[moduleId];
  
  if (!config) {
    return <Navigate to="/dashboard" replace />;
  }

  // Get the lazy component directly from config
  const LazyModule = config.component;

  return (
    <Suspense fallback={<ModuleLoadingFallback moduleName={config.name} />}>
      <LazyModule />
    </Suspense>
  );
};

// Individual Route Component for nested routes within modules
const ModuleRouteComponent = () => {
  const { moduleId, '*': subPath } = useParams<{ moduleId: string; '*': string }>();

  if (!moduleId) {
    return <Navigate to="/dashboard" replace />;
  }

  const config = modules[moduleId];
  if (!config) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!config.enabled) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Module Unavailable
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            The {config.name} module is currently disabled.
          </p>
        </div>
      </div>
    );
  }

  // Wrap content in RouteGuard to check menu access
  const content = (() => {
    // If no subpath, decide whether to show landing page or module component
    if (!subPath || subPath === '') {
      // If module has categories, show landing page; otherwise show the module directly
      if (config.categories && config.categories.length > 0) {
        return <ModuleLandingPage moduleId={moduleId} />;
      } else {
        return <LazyModuleComponent moduleId={moduleId} />;
      }
    }

    // For subpaths, load the actual module
    return <LazyModuleComponent moduleId={moduleId} />;
  })();

  return <RouteGuard>{content}</RouteGuard>;
};

export function ModuleRouter() {

  return (
    <Routes>
      {/* Dashboard Route */}
      <Route 
        path="/" 
        element={<LazyModuleComponent moduleId="dashboard" />}
      />

      {/* Pinned Items Route */}
      <Route 
        path="/pinned" 
        element={<PinnedLanding />}
      />

      {/* Recent Items Route */}
      <Route 
        path="/recent" 
        element={<RecentLanding />}
      />

      {/* Module Routes - Use parameter pattern */}
      <Route
        path="/:moduleId"
        element={<ModuleRouteComponent />}
      />
      <Route
        path="/:moduleId/*"
        element={<ModuleRouteComponent />}
      />

      {/* Remove legacy redirects - let module routes handle directly */}

      {/* 404 Route */}
      <Route
        path="*"
        element={
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Page Not Found
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                The page you're looking for doesn't exist.
              </p>
              <button 
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        }
      />
    </Routes>
  );
}